import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG or WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller');
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid="blog-image-upload" className="space-y-3">
      {value ? (
        <div className="relative w-full h-48 bg-slate-100 border border-slate-200 overflow-hidden">
          <img src={value} alt="Cover preview" data-testid="blog-image-preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            data-testid="blog-image-remove"
            title="Remove image"
            className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-700 p-1.5 shadow"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="blog-image-upload-button"
          className="w-full h-32 border-2 border-dashed border-slate-300 hover:border-primary hover:bg-slate-50 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500 disabled:opacity-50"
        >
          {uploading ? <Upload size={22} className="animate-pulse" /> : <ImageIcon size={22} />}
          <span className="text-sm">{uploading ? 'Uploading...' : 'Click to upload cover image (JPG, PNG, WebP · max 5MB)'}</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        data-testid="blog-image-file-input"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground whitespace-nowrap">or paste URL</span>
        <input
          type="url"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          data-testid="blog-image-url-input"
          className="flex-1 px-3 py-1.5 text-sm border border-slate-300 focus:ring-2 focus:ring-primary outline-none"
        />
      </div>
    </div>
  );
};

export default ImageUpload;
